/**
 * 거르개를 **주소에 싣는다.**
 *
 * ⚠ **화면 상태로만 들면 새로고침에 사라진다.** 관리자가 조건을 맞춰 놓고 한 줄을
 * 열어 본 뒤 뒤로 가면 처음부터 다시 골라야 하고, 「이 조건으로 보세요」를 남에게
 * 건넬 수도 없다. 주소에 실으면 둘 다 공짜로 된다.
 *
 * ⚠ **거르개가 바뀌면 쪽 값을 버린다**(명세 2-7 — 레거시 `cursor` 자리에 `offset`·`page`).
 * 다른 목록의 한가운데를 가리키던 쪽 값으로 새 목록을 이어 읽으면 엉뚱한 데서 시작한다.
 */
import { useSearchParams } from 'react-router-dom'
import { useCallback, useId, useMemo, useState } from 'react'
import { DATE_FORMAT, localDayRange } from '@/format'
import { CalendarButton } from './DateFields'

export interface SelectOption {
  value: string
  label: string
}

/** 쪽 값의 이름 — offset 방식과 page 방식(계약 0-6). */
const PAGE_KEYS = ['offset', 'page'] as const

function nonNegative(v: string | null): number {
  if (v === null || !/^\d+$/.test(v)) return 0
  return Number(v)
}

/** 주소의 조회 문자열을 읽고 쓰는 자리. */
export function useFilters(names: readonly string[]) {
  const [params, setParams] = useSearchParams()

  const values = useMemo(() => {
    const out: Record<string, string> = {}
    for (const n of names) out[n] = params.get(n) ?? ''
    return out
  }, [params, names])

  const set = useCallback((name: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(name, value)
    else next.delete(name)
    // ⚠ 조건이 바뀌면 쪽 값은 뜻을 잃는다.
    for (const k of PAGE_KEYS) next.delete(k)
    setParams(next, { replace: true })
  }, [params, setParams])

  const setPaging = useCallback((key: 'offset' | 'page', value: number) => {
    const next = new URLSearchParams(params)
    if (value > 0) next.set(key, String(value))
    else next.delete(key)
    setParams(next, { replace: true })
  }, [params, setParams])

  /** 탭을 바꿀 때 — 쪽 값만 지운다(명세 2-7). */
  const resetPaging = useCallback(() => {
    const next = new URLSearchParams(params)
    for (const k of PAGE_KEYS) next.delete(k)
    setParams(next, { replace: true })
  }, [params, setParams])

  const clear = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams])

  const active = names.some((n) => (values[n] ?? '') !== '')

  return {
    values, set, clear, active, resetPaging,
    offset: nonNegative(params.get('offset')),
    setOffset: (v: number) => setPaging('offset', v),
    page: nonNegative(params.get('page')),
    setPage: (v: number) => setPaging('page', v),
  }
}

/** 숫자만 든 거르개 값 → 숫자, 아니면 undefined(보내지 않는다). */
export function idParam(v: string | undefined): number | undefined {
  return v !== undefined && /^\d+$/.test(v) ? Number(v) : undefined
}

const CONTROL =
  'rounded border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800'

export function FilterBar({ children, onClear, active }: {
  children: React.ReactNode; onClear: () => void; active: boolean
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      {children}
      {active && (
        <button type="button" onClick={onClear}
          className="pb-1.5 text-xs text-slate-500 underline">Clear filters</button>
      )}
    </div>
  )
}

export function Search({ label, value, onChange, placeholder, inputMode }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
  inputMode?: 'numeric'
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <input value={value} placeholder={placeholder} className={CONTROL} inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

/**
 * 날짜만 받는 거르개 칸 — 늘 「yyyy-mm-dd」로 보이고 그렇게 친다(사장님 2026-10-01 「시간 표기는
 * yyyy-mm-dd HH:mm:ss」 — 날짜 칸은 그 앞 반). 옆 달력 단추로 웹이 그리는 영어 달력을 연다(DateFields·Calendar — 2026-10-02
 * 사장님 「1」, 그 전에는 브라우저 날짜 고르개). 「Clear」는 빈 값을 받는 칸(`allowEmpty`)에만 있다.
 *
 * ⚠ 브라우저 날짜 칸(type=date)을 보이는 칸으로 쓰지 않는다. 브라우저 언어에 따라 「2026. 10. 01.」처럼 보인다.
 * ⚠ 반쯤 친 글로 조회하지 않는다. 있는 날로 읽힐 때만 `onChange` 를 부르고, 칸을 떠날 때 읽히지 않는
 * 글이면 지금 조회 중인 날(`value`)로 되돌린다. `allowEmpty` 면 비운 것도 알린다(기간의 한쪽을 풀 때).
 */
export function DateInput({ label, name, value, onChange, allowEmpty = false }: {
  label: string
  /** 달력 단추 이름(「{name} 달력」). 없으면 칸 이름. */
  name?: string
  value: string
  onChange: (v: string) => void
  allowEmpty?: boolean
}) {
  const id = useId()
  const [draft, setDraft] = useState(value)
  // 주소가 바뀌면(거르개 지우기·뒤로 가기) 칸도 따라간다 — 렌더 중에 고치는 React 방식.
  const [shown, setShown] = useState(value)
  if (value !== shown) {
    setShown(value)
    setDraft(value)
  }
  const take = (v: string) => {
    setDraft(v)
    if (v === value) return
    if (localDayRange(v) !== null || (allowEmpty && v === '')) onChange(v)
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <label htmlFor={id} className="font-medium text-slate-600 dark:text-slate-300">{label}</label>
      <div className="flex items-stretch gap-1">
        <input id={id} value={draft} placeholder={DATE_FORMAT} maxLength={10} className={`${CONTROL} w-32`}
          onChange={(e) => take(e.target.value)}
          onBlur={() => setDraft(value)} />
        <CalendarButton kind="date" name={name ?? label} value={draft} clearable={allowEmpty} onPick={take} />
      </div>
    </div>
  )
}

export function Choice({ label, value, onChange, options, anyLabel }: {
  label: string; value: string; onChange: (v: string) => void
  options: readonly SelectOption[]; anyLabel?: string
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <select value={value} className={CONTROL} onChange={(e) => onChange(e.target.value)}>
        <option value="">{anyLabel ?? 'All'}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

/** 표시명과 canonical 값을 짝지어 고르개를 만든다. */
export function optionsOf<K extends string | number>(labels: Record<K, string>): SelectOption[] {
  return (Object.keys(labels) as K[]).map((k) => ({ value: String(k), label: labels[k] }))
}
